import React, { useState } from 'react';
import { adminApi } from '../../api/client';
import type { UserDetailsResponse } from '../../schemas/validation';

interface SubscriptionCardProps {
  subscription: UserDetailsResponse['data']['subscription'];
  userId?: string;
  onPlanChanged?: () => void;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  userId,
  onPlanChanged,
}) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [assigningPlan, setAssigningPlan] = useState(false);
  const [planMessage, setPlanMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const isAdminAssigned =
    subscription?.subscription && !subscription.subscription.stripeSubscriptionId;
  const hasStripeSubscription = !!subscription?.subscription?.stripeSubscriptionId;

  const handleAssignPlan = async () => {
    if (!userId || !selectedPlan) return;

    setAssigningPlan(true);
    setPlanMessage(null);

    try {
      const response = await adminApi.assignUserPlan(userId, selectedPlan);
      setPlanMessage({ type: 'success', text: response.message });
      setSelectedPlan('');
      onPlanChanged?.();
    } catch (err: any) {
      setPlanMessage({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Failed to assign plan',
      });
    } finally {
      setAssigningPlan(false);
    }
  };

  const handleRemovePlan = async () => {
    if (!userId) return;

    if (
      !confirm(
        'Are you sure you want to remove this admin-assigned plan? The user will be downgraded to FREE tier.'
      )
    ) {
      return;
    }

    setAssigningPlan(true);
    setPlanMessage(null);

    try {
      const response = await adminApi.removeUserPlan(userId);
      setPlanMessage({ type: 'success', text: response.message });
      onPlanChanged?.();
    } catch (err: any) {
      setPlanMessage({
        type: 'error',
        text: err.response?.data?.error || err.message || 'Failed to remove plan',
      });
    } finally {
      setAssigningPlan(false);
    }
  };

  if (!subscription) return null;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Subscription Details</h3>
      </div>
      <div className="px-6 py-4">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Current Plan</dt>
            <dd className="mt-1">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  subscription.plan?.name === 'PROFESSIONAL'
                    ? 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800'
                    : subscription.plan?.name === 'STARTER'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                }`}
              >
                {subscription.plan?.displayName || 'Free'}
              </span>
            </dd>
          </div>
          {subscription.plan && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Plan Price</dt>
              <dd className="mt-1 text-sm text-gray-900">
                ${subscription.plan.priceMonthly}/
                {(subscription.plan as any).billingInterval === 'year' ? 'year' : 'month'}
                {(subscription.plan as any).hasCustomPrice && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Custom Price
                  </span>
                )}
              </dd>
            </div>
          )}
          {subscription.subscription && (
            <>
              <div>
                <dt className="text-sm font-medium text-gray-500">Subscription Status</dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      subscription.subscription.canceledAt
                        ? 'bg-yellow-100 text-yellow-800'
                        : subscription.subscription.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : subscription.subscription.status === 'trialing'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {subscription.subscription.canceledAt
                      ? 'Cancelled'
                      : subscription.subscription.status.charAt(0).toUpperCase() +
                        subscription.subscription.status.slice(1)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Billing Period</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(subscription.subscription.currentPeriodStart).toLocaleDateString()} -{' '}
                  {new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString()}
                </dd>
              </div>
              {subscription.subscription.canceledAt && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Cancellation</dt>
                  <dd className="mt-1 text-sm text-red-600">
                    Cancelled on{' '}
                    {new Date(subscription.subscription.canceledAt).toLocaleDateString()}
                    {subscription.subscription.cancelAtPeriodEnd &&
                      ` - Access until ${new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString()}`}
                  </dd>
                </div>
              )}
            </>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Sites Usage</dt>
            <dd className="mt-1">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900">
                  {subscription.usage.sites} / {subscription.limits.maxSites}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                  <div
                    className={`h-2 rounded-full ${
                      subscription.usage.sites >= subscription.limits.maxSites
                        ? 'bg-red-600'
                        : subscription.usage.sites >= subscription.limits.maxSites * 0.8
                          ? 'bg-yellow-500'
                          : 'bg-blue-600'
                    }`}
                    style={{
                      width: `${Math.min((subscription.usage.sites / subscription.limits.maxSites) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Max Pages per Site</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {subscription.limits.maxPagesPerSite === -1
                ? 'Unlimited'
                : subscription.limits.maxPagesPerSite}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-sm font-medium text-gray-500 mb-2">Features</dt>
            <dd className="flex flex-wrap gap-2">
              {subscription.limits.canUseDomain && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Custom Domain
                </span>
              )}
              {subscription.limits.canUseContactForms && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Contact Forms
                </span>
              )}
              {subscription.limits.canUseAdvancedComponents && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Advanced Components
                </span>
              )}
              {subscription.limits.canUseCompeteStudio && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  Compete Studio
                </span>
              )}
            </dd>
          </div>
        </dl>

        {/* Admin Plan Assignment Section */}
        {userId && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Admin Plan Assignment</h4>

            {isAdminAssigned && (
              <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mr-2">
                    Admin Assigned
                  </span>
                  <span className="text-sm text-yellow-700">
                    This plan was manually assigned by an admin (no Stripe payment)
                  </span>
                </div>
              </div>
            )}

            {hasStripeSubscription && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-sm text-blue-700">
                  This user has an active Stripe subscription. To change their plan, use Stripe
                  dashboard or cancel their subscription first.
                </span>
              </div>
            )}

            {planMessage && (
              <div
                className={`mb-3 p-3 rounded-md ${
                  planMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <span className="text-sm">{planMessage.text}</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <select
                value={selectedPlan}
                onChange={e => setSelectedPlan(e.target.value)}
                disabled={assigningPlan || hasStripeSubscription}
                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select a plan...</option>
                <option value="FREE">FREE</option>
                <option value="STARTER">STARTER ($29/mo features)</option>
                <option value="PROFESSIONAL">PROFESSIONAL ($99/mo features)</option>
              </select>

              <button
                onClick={handleAssignPlan}
                disabled={!selectedPlan || assigningPlan || hasStripeSubscription}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningPlan ? 'Assigning...' : 'Assign Plan'}
              </button>

              {isAdminAssigned && (
                <button
                  onClick={handleRemovePlan}
                  disabled={assigningPlan}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove Plan
                </button>
              )}
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Assign a plan without requiring Stripe payment. Useful for partners, testers, or
              special cases.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
